import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { Alert, Button, Card, SearchableSelect, useToast, v, type SearchableSelectOption } from '@kss/ui';
import { Save, ArrowLeft } from 'lucide-react';
import type { AttributeValue, AttributeValueCreate } from '@kss/api';
import { attributeValueService, attributeService } from '@kss/api';

const loadAttributeOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await attributeService.all();
  return (rows ?? []).map((a) => ({ value: a.attributeId ?? '', label: a.name, description: a.description, disabled: !a.attributeId }));
};

const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function NewAttributeValue() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const isEdit = !!id && id !== 'new';
  const [form, setForm] = useState({ attributeId: "", value: "" });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const errs: Record<string, string | null> = {
    attributeId: v.requiredSelection("Attribute")(form.attributeId),
    value: v.runRules(form.value, [v.required("Value"), v.maxLength(120, "Value"), ...v.safeText("Value")]),
  };
  const fErr = (k: string) => (touched[k] ? errs[k] ?? undefined : undefined);
  const markF = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const formValid = !Object.values(errs).some(Boolean);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let c = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try { const r = await attributeValueService.get(id); if (!c) setForm({ attributeId: r.attributeId ?? '', value: r.value ?? '' }); }
        catch (e) { const msg = e instanceof Error ? e.message : 'Not found'; setErr(msg); toastRef.current.error('Load failed', msg); }
        finally { if (!c) setLoading(false); }
      }
    }
    run(); return () => { c = true; };
  }, [id, isEdit]);

  const save = async () => {
    setTouched({ attributeId: true, value: true });
    const problem = Object.values(errs).find(Boolean);
    if (problem) { toast.error("Fix errors", problem); return; }
    setSaving(true);
    try {
      if (isEdit && id) { await attributeValueService.update([{ attributeValueId: id, attributeId: form.attributeId, value: form.value } as AttributeValue]); toast.success('Saved', 'Value updated'); }
      else { await attributeValueService.create([{ attributeId: form.attributeId, value: form.value } as AttributeValueCreate]); toast.success('Saved', 'Value added'); }
      navigate('/panel/attribute-values');
    } catch (e) { toast.error('Save failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/attribute-values')}><ArrowLeft className="h-4 w-4" /></Button>
          <h2 className="text-base font-semibold text-content-primary">{isEdit ? 'Edit attribute value' : 'New attribute value'}</h2>
        </div>
        {loading ? <div className="text-sm text-content-muted">Loading…</div> : (
          <div className="grid gap-3 md:grid-cols-2">
            <SearchableSelect label="Attribute" value={form.attributeId} onChange={(v) => setForm((f) => ({ ...f, attributeId: v }))}
              loadOptions={loadAttributeOptions} placeholder="Select an attribute" searchPlaceholder="Search attributes..." required />
            <label className="flex flex-col gap-1"><span className="text-xs font-medium text-content-secondary">Value <span className="text-danger">*</span></span>
              <input value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} onBlur={markF("value")} placeholder="Red" className={inputCls + (fErr("value") ? " border-danger" : "")} />
              {fErr("value") && <span className="text-xs text-danger">{fErr("value")}</span>}</label>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate('/panel/attribute-values')}>Cancel</Button>
          <Button onClick={save} disabled={saving || !formValid}><Save className="h-4 w-4" />{saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}</Button>
        </div>
      </Card>
    </div>
  );
}
